/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ClipboardIcon } from '@radix-ui/react-icons';
import { PageTitle, StatusBadge, Text, UnstyledButton } from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';
import type { LaunchStatus } from '../types';
import { LAUNCH_LIFECYCLE_BADGE } from './launch-unit-status';
import { navIconFor } from '@layout/agency/agency-nav';

export interface LaunchBoardHeaderProps {
  title: string;
  status?: LaunchStatus;
  canEdit?: boolean;
  onEdit?: () => void;
  canActivate?: boolean;
  onActivate?: () => void;
  canClose?: boolean;
  onCloseLaunch?: () => void;
}

/**
 * Navigates back to the agency launches list.
 *
 * @param agencyId - Agency id from route params.
 * @param navigate - React Router navigate.
 */
function goToLaunchesList(
  agencyId: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
): void {
  if (agencyId) {
    navigate(`/app/agencies/${agencyId}/launches`);
    return;
  }
  navigate('..');
}

/**
 * Board chrome: breadcrumb, header card with title/status, edit/close actions.
 *
 * @param props - Title and optional manage actions.
 * @returns Board page header.
 */
export function LaunchBoardHeader({
  title,
  status,
  canEdit = false,
  onEdit,
  canActivate = false,
  onActivate,
  canClose = false,
  onCloseLaunch,
}: LaunchBoardHeaderProps) {
  const { t } = useTranslation('features/launches');
  const navigate = useNavigate();
  const { agency_id } = useParams<{ agency_id: string }>();

  return (
    <div className="flex flex-col gap-(--space-4)" data-testid="launch-board-header">
      <nav
        data-testid="launch-board-breadcrumb"
        aria-label={t('list.title')}
        className="flex items-center gap-(--space-1) text-[length:var(--text-small)] text-(--text-tertiary)"
      >
        <UnstyledButton
          type="button"
          data-testid="launch-board-back"
          onClick={() => goToLaunchesList(agency_id, navigate)}
          className="flex cursor-pointer items-center gap-[0.125rem] border-none bg-transparent p-0 text-(--text-tertiary) hover:text-(--text-primary)"
        >
          <ChevronLeftIcon width={14} height={14} aria-hidden />
          {t('list.title')}
        </UnstyledButton>
        <Text component="span" aria-hidden>/</Text>
        <Text component="span" className="font-medium text-(--text-primary)">{title}</Text>
      </nav>

      <div
        data-testid="launch-board-header-card"
        className={[
          'flex flex-wrap items-center gap-(--space-3)',
          'rounded-(--radius-lg) border border-(--border-subtle)',
          'bg-[color-mix(in_srgb,var(--seed-base)_3%,var(--surface-primary))]',
          'p-(--space-4)',
          'shadow-[var(--shadow-md)]',
        ].join(' ')}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-(--space-2)">
          <PageTitle
            data-testid="launch-board-title"
            icon={navIconFor('launches')}
          >
            {title}
          </PageTitle>
          {status ? (
            <Text component="span" data-testid="launch-board-status">
              <StatusBadge className={LAUNCH_LIFECYCLE_BADGE[status]}>
                {t(`launchStatus.${status}`)}
              </StatusBadge>
            </Text>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-(--space-2)">
          {canEdit && onEdit ? (
            <Button data-testid="launch-board-edit" variant="solid" onClick={onEdit}>
              {t('board.edit')}
            </Button>
          ) : null}
          {canActivate && onActivate ? (
            <Button data-testid="launch-board-activate" variant="solid" onClick={onActivate}>
              {t('board.activate')}
            </Button>
          ) : null}
          {canClose && onCloseLaunch ? (
            <Button
              data-testid="launch-board-close"
              variant="outline"
              onClick={onCloseLaunch}
            >
              {t('board.close')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
